/**
 * What a CSR is told when a resend or a contact edit did not succeed (BON-472).
 *
 * THE WHOLE FILE TURNS ON ONE FACT ABOUT THE BACKEND: every 4xx these two
 * endpoints answer is decided BEFORE anything is written. Authorisation runs
 * first, and all eight state refusals are settled before the save. So a 400,
 * 401, 403, 404 or 409 means, without qualification, that nothing happened —
 * and the CSR can say so.
 *
 * A 5xx, or no response at all, is the opposite: the request may well have been
 * carried out and the answer lost on the way back. That is the ONLY case that
 * gets uncertain wording, and it has to stop the CSR reassuring the customer,
 * because a rotation that committed has already killed the link the customer is
 * holding.
 *
 * NOTHING HERE EVER RENDERS A RAW EXCEPTION MESSAGE. `Failed to fetch` under a
 * heading that reads "nothing was sent" is the single worst thing this surface
 * can say. `ReservationError.message` is not a raw exception message — the API
 * client resolves it to the server's own CSR-facing sentence, or to that
 * endpoint's fallback, before it is ever thrown.
 */
import { NO_RESPONSE_STATUS, ReservationError } from '../../api/reservations';

/** Which of the two actions failed. Picks the refusal title, and nothing else —
 *  the uncertain case reads the same either way. */
export type RotationAction = 'resend' | 'contact';

export interface RequestFailure {
  /** For a dialog. The inline form ignores it and shows `message` alone. */
  title: string;
  /** Written for a CSR, complete, and safe to show verbatim. */
  message: string;
  /**
   * The request may or may not have gone through. Callers use it to escalate:
   * an uncertain outcome is never a quiet inline note in a form the CSR is
   * about to close.
   */
  indeterminate: boolean;
  /** Whether the row on screen is now out of date and must be re-read. */
  refresh: boolean;
}

/** The uncertain case, worded identically for both actions — the CSR's problem
 *  is the same one either way, and it is not about which button they pressed. */
const UNKNOWN_TITLE = 'Not sure whether that went through';
const UNKNOWN_MESSAGE =
  'We couldn’t reach the server, so this may or may not have gone ahead. Reload the page and ' +
  'check the reservation before you tell the customer anything — if a new link was sent, the one ' +
  'they had has already stopped working.';

const REFUSAL_TITLE: Record<RotationAction, string> = {
  // Deliberately different sentences rather than the same one rearranged. The
  // other dialog a CSR can land on says the link DID change, and under pressure
  // the title is the only part that gets read.
  resend: 'Nothing was sent',
  contact: 'Nothing was changed',
};

/** Appended to the server's own sentence. The reason alone says what the server
 *  thought; this says what it means for the customer on the phone. */
const NOTHING_HAPPENED = 'Nothing has been changed.';

/** Whether the server got far enough to decide anything. `NO_RESPONSE_STATUS`
 *  is a request that never landed; 5xx is one that landed and then broke, which
 *  says nothing about whether the write ran. */
function isIndeterminate(status: number): boolean {
  return status === NO_RESPONSE_STATUS || status >= 500;
}

export function describeRequestFailure(action: RotationAction, err: unknown): RequestFailure {
  const refusal = err instanceof ReservationError ? err : null;
  if (!refusal || isIndeterminate(refusal.status)) {
    return {
      title: UNKNOWN_TITLE,
      message: UNKNOWN_MESSAGE,
      indeterminate: true,
      // Re-read regardless: it is the only way the CSR finds out which way it
      // went, and the message tells them to look.
      refresh: true,
    };
  }
  return {
    title: REFUSAL_TITLE[action],
    message: `${refusal.message} ${NOTHING_HAPPENED}`,
    indeterminate: false,
    // A STATE refusal means the row on screen is older than the server's answer.
    // A 400/401/403 is about the request or the caller, so there is nothing new
    // to read.
    refresh: Boolean(refusal.code),
  };
}
